import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle, Package, Truck, ShieldCheck, Handshake, ArrowRight, Star, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/pages/Overview";

const STAGES = [
  { key: "matched", label: "Deal Matched", icon: Handshake },
  { key: "payment_secured", label: "Payment Confirmed", icon: ShieldCheck },
  { key: "in_transport", label: "In Transportation", icon: Truck },
  { key: "delivered", label: "Delivered", icon: CheckCircle },
];

export default function DealDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [deal, setDeal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dispatch, setDispatch] = useState({ transporter: "", vehicle_no: "" });
  const [rating, setRating] = useState({ show: false, val: 5, review: "" });

  const load = async () => {
    try {
      const { data } = await api.get(`/deals/${id}`);
      setDeal(data);
    } catch (e) {
      toast.error("Deal not found");
      nav("/app/deals");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!deal) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-40 shimmer rounded" />
        <div className="h-40 shimmer rounded-xl" />
        <div className="h-64 shimmer rounded-xl" />
      </div>
    );
  }

  const currentIdx = STAGES.findIndex((s) => s.key === deal.status);
  const progressPct = currentIdx < 0 ? 0 : ((currentIdx + 1) / STAGES.length) * 100;

  const advance = async (nextStatus, extra = {}) => {
    if (["cancelled", "disputed"].includes(nextStatus) && !window.confirm(`Are you sure? This will mark the deal as ${nextStatus}.`)) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/deals/${id}/status`, { status: nextStatus, ...extra });
      setDeal(data);
      toast.success(`Deal → ${nextStatus.replace(/_/g, " ")}`);
      if (nextStatus === "delivered") setRating((r) => ({ ...r, show: true }));
    } catch (ex) {
      toast.error(formatErr(ex?.response?.data?.detail));
    }
    setBusy(false);
  };

  // the steps THIS user may take now come from the server (who may do what depends on buyer, seller or admin)
  const actions = deal.actions || [];
  const primary = actions.find((a) => !["cancelled", "disputed"].includes(a.status));
  const others = actions.filter((a) => a !== primary);

  const submitRating = async () => {
    try {
      const rated = user.id === deal.buyer_id ? deal.seller_id : deal.buyer_id;
      await api.post("/ratings", { deal_id: deal.id, rated_user_id: rated, rating: rating.val, review_text: rating.review });
      toast.success("Rating submitted");
      setRating({ show: false, val: 5, review: "" });
    } catch (ex) {
      toast.error(formatErr(ex?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="deal-detail-page">
      <Link to="/app/deals" className="inline-flex items-center gap-2 text-sm font-semibold text-stone-600 hover:text-emerald-900">
        <ArrowLeft size={16} /> All Deals
      </Link>

      {/* Header */}
      <div className="rounded-xl bg-white border border-stone-200 p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-orange-600">Deal · {deal.id.slice(-8)}</div>
            <div className="font-display text-3xl font-black text-stone-900 mt-1">{deal.commodity} · {deal.quantity} {deal.unit}</div>
            <div className="text-sm text-stone-500 mt-1">Seller: <b>{deal.seller_name}</b> → Buyer: <b>{deal.buyer_name}</b></div>
          </div>
          <div className="text-right">
            <div className="font-display text-3xl font-black text-emerald-900">₹{deal.total_value.toLocaleString("en-IN")}</div>
            <div className="text-xs text-stone-500">Service fee ₹{deal.service_fee.toLocaleString("en-IN")} · 1.1%</div>
          </div>
        </div>

        {/* Progress tracker */}
        <div className="mt-8">
          <div className="relative h-1.5 rounded-full bg-stone-200 overflow-hidden">
            <motion.div
              initial={{ width: 0 }} animate={{ width: `${progressPct}%` }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="progress-bar-fill absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-800 to-orange-500"
            />
          </div>
          <div className="mt-4 grid grid-cols-4 gap-2">
            {STAGES.map((s, i) => {
              const done = i <= currentIdx;
              const active = i === currentIdx;
              return (
                <div key={s.key} className={`flex flex-col items-center text-center ${done ? "opacity-100" : "opacity-50"}`}>
                  <motion.div
                    initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: i * 0.1 }}
                    className={`w-10 h-10 rounded-full grid place-items-center border-2 ${active ? "bg-orange-500 border-orange-500 text-white" : done ? "bg-emerald-800 border-emerald-800 text-white" : "bg-white border-stone-300 text-stone-400"}`}
                  >
                    <s.icon weight="bold" size={18} />
                  </motion.div>
                  <div className={`mt-2 text-[11px] font-bold uppercase tracking-widest ${done ? "text-stone-800" : "text-stone-400"}`}>{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action */}
        {actions.length > 0 && (
          <div className="mt-6 flex items-center justify-end flex-wrap gap-3">
            {primary?.status === "in_transport" && (
              <>
                <input value={dispatch.transporter} onChange={(e) => setDispatch((d) => ({ ...d, transporter: e.target.value }))} placeholder="Transporter" className="px-3 py-2 rounded-md border border-stone-300 text-sm" data-testid="dispatch-transporter" />
                <input value={dispatch.vehicle_no} onChange={(e) => setDispatch((d) => ({ ...d, vehicle_no: e.target.value }))} placeholder="Vehicle no." className="px-3 py-2 rounded-md border border-stone-300 text-sm w-36" data-testid="dispatch-vehicle" />
              </>
            )}
            {others.map((a) => (
              <button key={a.status} disabled={busy} onClick={() => advance(a.status)}
                className="px-4 py-2.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50 text-sm font-bold disabled:opacity-60" data-testid={`deal-action-${a.status}`}>
                {a.label}
              </button>
            ))}
            {primary && (
              <button
                disabled={busy} onClick={() => advance(primary.status, primary.status === "in_transport" ? dispatch : {})}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-emerald-900 hover:bg-emerald-800 text-white font-bold disabled:opacity-60 transition-colors"
                data-testid="advance-status-btn"
              >
                {primary.label} <ArrowRight size={16} weight="bold" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Detail grid */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-xl bg-white border border-stone-200 p-6">
          <div className="text-xs uppercase tracking-widest font-bold text-stone-500">Deal Terms</div>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <Info k="Commodity" v={deal.commodity} />
            <Info k="Quantity" v={`${deal.quantity} ${deal.unit}`} />
            <Info k="Rate" v={`₹${deal.agreed_price}/${deal.unit}`} />
            <Info k="Subtotal" v={`₹${deal.total_value.toLocaleString("en-IN")}`} />
            <Info k="Service Fee (1.1%)" v={`₹${deal.service_fee.toLocaleString("en-IN")}`} />
            <Info k="Status" v={<StatusBadge status={deal.status} />} />
          </div>

          <div className="mt-6 border-t border-stone-100 pt-4">
            <div className="text-xs uppercase tracking-widest font-bold text-stone-500 mb-2">Timeline</div>
            <div className="space-y-2">
              {(deal.status_history || []).map((h, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-emerald-700" />
                  <div className="capitalize font-semibold text-stone-800">{h.status.replace(/_/g, " ")}</div>
                  <div className="ml-auto text-xs text-stone-500 font-mono">{new Date(h.at).toLocaleString("en-IN")}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Info card */}
        <div className="rounded-xl bg-emerald-950 text-white p-6">
          <div className="text-xs uppercase tracking-widest font-bold text-orange-400">Deal Snapshot</div>
          <div className="mt-2 font-display text-2xl font-black">
            {deal.status === "matched" && "Awaiting payment confirmation"}
            {deal.status === "payment_secured" && "Payment Confirmed"}
            {deal.status === "in_transport" && "In Transit"}
            {deal.status === "delivered" && "Delivered"}
            {deal.status === "disputed" && "In dispute"}
            {deal.status === "cancelled" && "Cancelled"}
          </div>
          <div className="text-emerald-200 text-sm mt-1">₹{deal.total_value.toLocaleString("en-IN")} · {deal.quantity} {deal.unit}</div>
          <div className="mt-6 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-emerald-100"><CheckCircle weight="fill" size={16} /> Payment is confirmed by the India Mandi team once the money is received</div>
            <div className="flex items-center gap-2 text-emerald-100"><Package weight="fill" size={16} /> The seller enters the transporter and vehicle when dispatching</div>
            <div className="flex items-center gap-2 text-emerald-100"><Receipt weight="fill" size={16} /> A draft invoice is made on delivery; the seller issues the GST tax invoice</div>
          </div>

          {deal.status === "delivered" && (
            <Link to={`/app/invoices`} className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-orange-600 hover:bg-orange-700 text-white font-bold text-sm">
              <Receipt size={16} /> View Invoice
            </Link>
          )}
        </div>
      </div>

      {/* Rate counterparty */}
      {rating.show && (
        <div className="rounded-xl bg-white border border-stone-200 p-6" data-testid="rating-panel">
          <div className="text-xs uppercase tracking-widest font-bold text-orange-600">Rate the counterparty</div>
          <div className="font-display text-xl font-bold mt-1">How was your experience?</div>
          <div className="mt-4 flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => setRating((r) => ({ ...r, val: n }))} data-testid={`rating-${n}`}>
                <Star weight={n <= rating.val ? "fill" : "regular"} size={30} className={n <= rating.val ? "text-orange-500" : "text-stone-300"} />
              </button>
            ))}
          </div>
          <textarea
            value={rating.review} onChange={(e) => setRating((r) => ({ ...r, review: e.target.value }))} rows={2}
            placeholder="Optional review (e.g., quick delivery, fair grader)…"
            className="mt-3 w-full px-3 py-2 rounded-md border border-stone-300 text-sm"
            data-testid="rating-review"
          />
          <button onClick={submitRating} className="mt-3 px-4 py-2 rounded-md bg-emerald-900 hover:bg-emerald-800 text-white font-bold text-sm" data-testid="submit-rating-btn">
            Submit Rating
          </button>
        </div>
      )}
    </div>
  );
}

function Info({ k, v }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">{k}</div>
      <div className="mt-1 font-semibold text-stone-800">{v}</div>
    </div>
  );
}
